"use client";
import Link from "next/link";
import { GetHomeUserCommunities } from "~/app/my-communities/actions";
import { If } from "~/components/v3/Conditional";
import HomeCreateCommunity from "./HomeCreateCommunity";
import { Button } from "~/components/v4/themed/Button";
import { Card } from "~/components/v4/shadcn/Card";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import RiverStar from "~/components/v4/icons/RiverStar";

type Props = {
    myCommunities?: GetHomeUserCommunities;
};

const HomeMyCommunitiesV2 = ({ myCommunities }: Props) => {
    return (
        <div className="flex flex-col gap-6">
            <If condition={!!myCommunities?.managedCommunities?.length}>
                <div className="space-y-4">
                    <span className="text-base font-[500]">My Communities</span>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {myCommunities?.managedCommunities?.map((community) => (
                            <Link
                                href={`/beta/${community.slug}`}
                                key={community.id}
                            >
                                <Card className="flex h-full cursor-pointer flex-col rounded-lg bg-v3-N500 p-4 transition-opacity hover:opacity-80">
                                    <div className="mb-2 flex items-center gap-3">
                                        <CommunityAvatar
                                            community={{
                                                id: community.id,
                                                name: community.name,
                                                image: community.image,
                                            }}
                                            width={40}
                                            height={40}
                                        />
                                        <span className="text-sm font-medium text-white">
                                            {community.name}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-2 text-xs text-gray-400">
                                        <span>
                                            {community._count?.followers.toLocaleString(
                                                "en-US",
                                            ) || 0}{" "}
                                            members
                                        </span>
                                        <RiverStar
                                            className="inline-block flex-shrink-0"
                                            color="#A3A8B4"
                                        />
                                        <span>
                                            {community._count.events.toLocaleString(
                                                "en-US",
                                            ) || 0}{" "}
                                            upcoming{" "}
                                            {community._count.events === 1
                                                ? "event"
                                                : "events"}
                                        </span>
                                    </div>
                                </Card>
                            </Link>
                        ))}
                    </div>
                </div>
            </If>

            <If condition={!!myCommunities?.followedCommunities?.length}>
                <div className="space-y-4">
                    <span className="text-base font-[500]">
                        Followed Communities
                    </span>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {myCommunities?.followedCommunities?.map(
                            (community) => (
                                <Link
                                    href={`/beta/${community.slug}`}
                                    key={community.id}
                                >
                                    <Card className="flex h-full cursor-pointer flex-col rounded-lg bg-v3-N500 p-4 transition-opacity hover:opacity-80">
                                        <div className="mb-2 flex items-center gap-3">
                                            <CommunityAvatar
                                                community={{
                                                    id: community.id,
                                                    name: community.name,
                                                    image: community.image,
                                                }}
                                                width={40}
                                                height={40}
                                            />
                                            <span className="text-sm font-medium text-white">
                                                {community.name}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 text-xs text-gray-400">
                                            <span>
                                                {community._count?.followers.toLocaleString(
                                                    "en-US",
                                                ) || 0}{" "}
                                                members
                                            </span>
                                            <RiverStar
                                                className="inline-block flex-shrink-0"
                                                color="#A3A8B4"
                                            />
                                            <span>
                                                {community._count.events.toLocaleString(
                                                    "en-US",
                                                ) || 0}{" "}
                                                upcoming{" "}
                                                {community._count.events === 1
                                                    ? "event"
                                                    : "events"}
                                            </span>
                                        </div>
                                    </Card>
                                </Link>
                            ),
                        )}
                    </div>
                </div>
            </If>

            <If
                condition={
                    !myCommunities?.managedCommunities?.length &&
                    !myCommunities?.followedCommunities?.length
                }
            >
                <div className="flex flex-col gap-2">
                    <p>
                        River is built around communities, making it easy to
                        find like-minded people and plan gatherings around the
                        world.
                    </p>
                    <div className="flex flex-row items-center gap-2">
                        <Link
                            href="/discovery/communities"
                            className="inline-flex"
                        >
                            <Button
                                variant="primaryLink"
                                className="m-0 inline p-0"
                            >
                                Browse communities
                            </Button>
                        </Link>
                        <span>or</span>
                        <HomeCreateCommunity />
                    </div>
                </div>
            </If>
        </div>
    );
};

export default HomeMyCommunitiesV2;
